import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useCallback } from 'react';
import { enforceMutualExclusivity } from '@/pages/PortfolioAnalysis/PortfolioGraphing/utils';
import AggregateLimit from './AggregateLimit';

const useStyles = makeStyles(theme => ({
  aggregateLayer: {
    borderRight: 'solid thin rgba(0, 0, 0, 0.12)',
    '&:last-child': {
      border: 'none',
    },
  },
  checkboxRoot: {
    width: '100%',
  },
  checkboxRowLabel: {
    width: '100%',
    justifyContent: 'space-between',
    margin: 0,
  },
  disabled: {
    color: 'rgba(0, 0, 0, 0.26)',
  },
  label: {
    ...theme.palette.alternateText,
    fontWeight: 'bold',
  },
  layerLabel: {
    width: '100%',
    borderBottom: 'solid thin rgba(0, 0, 0, 0.12)',
  },
  radioRow: {
    width: '100%',
    justifyContent: 'space-between',
    margin: 0,
  },
  radioRowLabel: {
    fontWeight: 'bold',
  },
}));

const Aggregates = ({
  aggregateLayers,
  aggregateTypes,
  handleSetAggregate,
  handleSetState,
  handleMultiplesSetState,
  includeSortLimit,
  itemIsDisabledParams = {},
  selectedAggregate,
  selectedCheckboxAggregates,
  selectedSortDirection,
  selectedGraphTypeOptions = [],
}) => {
  const classes = useStyles();

  // Disabled function for treemap layer aggregates
  const isAggregateLayerDisabled = useCallback(
    (aggregate, i, layer) => {
      // Check if graph type is compatible with the aggregate
      const isDisabled = aggregate.isDisabled?.(itemIsDisabledParams);
      // If not, disable button and uncheck
      if (isDisabled) {
        if (selectedAggregate[layer] === aggregate.name) {
          handleSetState('', 'aggregate', layer);
        }
        return true;
      }
      // Check if aggregate is already selected in another layer
      const found = Object.values(selectedAggregate).indexOf(aggregate.name);
      return found !== -1 && found !== i;
    },
    [handleSetState, itemIsDisabledParams, selectedAggregate],
  );

  const aggregateIsDisabled = useCallback(
    aggregate => aggregate.isDisabled?.(itemIsDisabledParams),
    [itemIsDisabledParams],
  );

  const handleUnsetAggregateLayer = (i, aggregate, layer) => e => {
    if (i > 0 && e.target.value === aggregate.name) {
      handleSetState('', 'aggregate', layer);
    }
  };

  // Helper: Check if aggregate is selected in graphTypeOptions
  const isAggregateInGraphTypeOptions = useCallback(
    (aggregateName, graphTypeOptions) => graphTypeOptions.some(opt => opt.name === aggregateName),
    [],
  );

  // Helper: Get checked state for checkbox aggregate
  const getCheckboxCheckedState = useCallback(
    (aggregate, graphTypeOptions, checkboxAggregates) => {
      if (aggregate.syncsWithGraphTypeOptions) {
        return isAggregateInGraphTypeOptions(aggregate.name, graphTypeOptions);
      }
      return checkboxAggregates[aggregate.name];
    },
    [isAggregateInGraphTypeOptions],
  );

  // Helper: Handle checkbox change for synced aggregates
  const handleSyncedAggregateChange = useCallback(
    (aggregate, graphTypeOptions, checkboxAggregates) => {
      const isCurrentlyChecked = isAggregateInGraphTypeOptions(aggregate.name, graphTypeOptions);

      const newGraphTypeOptions = isCurrentlyChecked
        ? graphTypeOptions.filter(opt => opt.name !== aggregate.name)
        : [...graphTypeOptions, { name: aggregate.name }];

      const filteredOptions = enforceMutualExclusivity(newGraphTypeOptions, graphTypeOptions);

      handleMultiplesSetState({
        graphTypeOptions: filteredOptions,
        checkboxAggregates: {
          ...checkboxAggregates,
          [aggregate.name]: !isCurrentlyChecked,
        },
      });
    },
    [isAggregateInGraphTypeOptions, handleMultiplesSetState],
  );

  // Helper: Handle checkbox change for normal aggregates
  const handleNormalAggregateChange = useCallback(
    (aggregate, checkboxAggregates) => {
      handleSetState(
        {
          ...checkboxAggregates,
          [aggregate.name]: !checkboxAggregates[aggregate.name],
        },
        'checkboxAggregates',
      );
    },
    [handleSetState],
  );

  // Handler for checkbox onChange
  const handleCheckboxChange = useCallback(
    (aggregate, graphTypeOptions, checkboxAggregates) => () => {
      if (aggregate.syncsWithGraphTypeOptions) {
        handleSyncedAggregateChange(aggregate, graphTypeOptions, checkboxAggregates);
      } else {
        handleNormalAggregateChange(aggregate, checkboxAggregates);
      }
    },
    [handleSyncedAggregateChange, handleNormalAggregateChange],
  );

  return (
    <>
      {aggregateTypes && (
        <FormControl style={{ width: '100%', marginBottom: '5px' }}>
          <Typography className={classes.label} variant='overline'>
            Aggregate Data
          </Typography>
          {/* This renders the aggregate layer option seen on the portfolio analysis treemap */}
          {aggregateLayers ? (
            <>
              <Grid container direction='row' spacing={1}>
                {aggregateLayers.map((layer, i) => (
                  <Grid
                    className={classes.aggregateLayer}
                    container
                    direction='column'
                    key={`graph-aggregate-radio-layer-${layer}`}
                    size={4}
                  >
                    <Typography className={classes.layerLabel} variant='overline'>
                      {layer}
                    </Typography>
                    <RadioGroup
                      value={selectedAggregate[layer]}
                      name='graph-aggregate-radio-buttons-group'
                      onChange={e => handleSetState(e.target.value, 'aggregate', layer)}
                    >
                      <Grid
                        container
                        direction='column'
                        sx={{
                          justifyContent: 'space-around',
                        }}
                      >
                        {aggregateTypes
                          .filter(aggregate => aggregate.type !== 'checkbox')
                          .map(aggregate => (
                            <Grid key={`checklist-${aggregate.name}`}>
                              <Tooltip
                                title={
                                  typeof aggregate.tooltip === 'function'
                                    ? aggregate.tooltip(itemIsDisabledParams)
                                    : aggregate.tooltip || ''
                                }
                              >
                                <FormControlLabel
                                  classes={{ label: classes.radioRowLabel }}
                                  value={aggregate.name}
                                  control={
                                    <Radio
                                      onClick={handleUnsetAggregateLayer(i, aggregate, layer)}
                                    />
                                  }
                                  label={aggregate.name}
                                  disabled={isAggregateLayerDisabled(aggregate, i, layer)}
                                />
                              </Tooltip>
                            </Grid>
                          ))}
                      </Grid>
                    </RadioGroup>
                  </Grid>
                ))}
              </Grid>
              {/* Render checkboxes below the layers */}
              <Grid container spacing={1} sx={{ marginTop: 1 }}>
                {aggregateTypes
                  .filter(aggregate => aggregate.type === 'checkbox')
                  .map(aggregate => {
                    const tooltipText =
                      typeof aggregate.tooltip === 'function'
                        ? aggregate.tooltip(itemIsDisabledParams)
                        : aggregate.tooltip || '';
                    return (
                      <Tooltip key={`checkbox-${aggregate.name}`} title={tooltipText}>
                        <Grid size={12}>
                          <FormControl component='fieldset' className={classes.checkboxRoot}>
                            <FormControlLabel
                              className={classes.checkboxRowLabel}
                              labelPlacement='start'
                              value={true}
                              control={
                                <Checkbox
                                  color='primary'
                                  checked={getCheckboxCheckedState(
                                    aggregate,
                                    selectedGraphTypeOptions,
                                    selectedCheckboxAggregates,
                                  )}
                                  onChange={handleCheckboxChange(
                                    aggregate,
                                    selectedGraphTypeOptions,
                                    selectedCheckboxAggregates,
                                  )}
                                />
                              }
                              label={
                                <Typography className={classes.radioRowLabel}>
                                  {aggregate.name}
                                </Typography>
                              }
                              disabled={aggregateIsDisabled(aggregate)}
                            />
                          </FormControl>
                        </Grid>
                      </Tooltip>
                    );
                  })}
              </Grid>
            </>
          ) : (
            <Grid container spacing={1}>
              {/* This renders the normal aggregate types (non layered) */}
              {aggregateTypes.map(aggregate => {
                const tooltipText =
                  typeof aggregate.tooltip === 'function'
                    ? aggregate.tooltip(itemIsDisabledParams)
                    : aggregate.tooltip || '';
                return (
                  <Tooltip key={`checklist-${aggregate.name}`} title={tooltipText}>
                    <Grid size={12}>
                      {/* This renders aggregate types with options enabled
                     (allows user to select top or bottom x amount of traces) */}
                      {aggregate.options && !includeSortLimit ? (
                        <AggregateLimit
                          aggregate={aggregate}
                          aggregateIsDisabled={aggregateIsDisabled}
                          defaultSortDirection={selectedSortDirection}
                          handleSetAggregate={handleSetAggregate}
                          selectedAggregate={selectedAggregate}
                          styles={classes}
                        />
                      ) : aggregate.type === 'checkbox' ? (
                        // This renders aggregate checkboxes (can select multiple)
                        <FormControl component='fieldset' className={classes.checkboxRoot}>
                          <FormControlLabel
                            className={classes.checkboxRowLabel}
                            labelPlacement='start'
                            value={true}
                            control={
                              <Checkbox
                                color='primary'
                                checked={getCheckboxCheckedState(
                                  aggregate,
                                  selectedGraphTypeOptions,
                                  selectedCheckboxAggregates,
                                )}
                                onChange={handleCheckboxChange(
                                  aggregate,
                                  selectedGraphTypeOptions,
                                  selectedCheckboxAggregates,
                                )}
                              />
                            }
                            label={
                              <Typography className={classes.radioRowLabel}>
                                {aggregate.name}
                              </Typography>
                            }
                            disabled={aggregateIsDisabled(aggregate)}
                          />
                        </FormControl>
                      ) : (
                        // This renders the base aggregate (single option)
                        <RadioGroup
                          value={selectedAggregate}
                          name='graph-aggregate-radio-buttons-group'
                          onChange={() =>
                            aggregate.options
                              ? handleSetState(aggregate.name, 'aggregate')
                              : handleSetAggregate(aggregate.name)
                          }
                        >
                          <FormControlLabel
                            className={classes.radioRow}
                            classes={{
                              label: clsx({
                                [classes.radioRowLabel]: true,
                                [classes.disabled]: aggregateIsDisabled(aggregate),
                              }),
                            }}
                            checked={aggregate.name === selectedAggregate}
                            value={aggregate.name}
                            control={<Radio />}
                            disabled={aggregateIsDisabled(aggregate)}
                            label={
                              <Typography
                                className={clsx({
                                  [classes.radioRowLabel]: true,
                                  [classes.disabled]: aggregateIsDisabled(aggregate),
                                })}
                              >
                                {aggregate.name}
                              </Typography>
                            }
                            labelPlacement='start'
                          />
                        </RadioGroup>
                      )}
                    </Grid>
                  </Tooltip>
                );
              })}
            </Grid>
          )}
        </FormControl>
      )}
    </>
  );
};

export default Aggregates;
